import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CrossIcon from 'components/atoms/icons/crossIcon';

import s from './styles.module.scss';

import type { FC } from 'react';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

interface IProps {
  /** connection.sendDigits() — see connectService.sendDigits. */
  onDigit: (digit: string) => void;
  onHide: () => void;
}

/**
 * Replaces the tile row in place — NOT a second panel and NOT an overlay, so the
 * capacity readout and Link readings stay mounted above and below it.
 *
 * Reachable while the contact is still CONNECTING. Carriers and IVR spam gates
 * ask the caller to dial a digit before the call completes; gating DTMF on
 * isOnCall makes those calls impossible to complete.
 */
const Keypad: FC<IProps> = ({ onDigit, onHide }) => {
  const { t } = useTranslation();
  const [entered, setEntered] = useState('');

  const press = (digit: string) => {
    onDigit(digit);
    // Local echo only. There is no way to read back what the far end received,
    // so this is a record of what the agent pressed, not of what landed.
    setEntered((prev) => (prev + digit).slice(-16));
  };

  return (
    <>
      <div className={s['keypad-readout-wrapper']}>
        <div className={s['keypad-readout']}>
          <span className={s['keypad-digits']}>{entered}</span>
          {!!entered && (
            <button
              type="button"
              className={s['keypad-clear']}
              onClick={() => setEntered('')}
              aria-label={t('connectPanel.clearDigits')}
            >
              <CrossIcon width={13} height={13} />
            </button>
          )}
        </div>
      </div>

      <div className={s['keypad-grid']}>
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className={s['keypad-key']}
            onClick={() => press(key)}
            data-cy={`keypad-${key}`}
          >
            {key}
          </button>
        ))}
      </div>

      <div className={s['keypad-hide-wrapper']}>
        <button type="button" className={s['keypad-hide']} onClick={onHide}>
          {t('connectPanel.hideKeypad')}
        </button>
      </div>
    </>
  );
};

export default Keypad;
